import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import {
  LayoutDashboard, Users, LogOut, Sun, Moon,
  Bell, ChevronDown, Menu, X, Sparkles, User, Mail, MessageCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { db } from '../config/firebase';
import { PageHeaderProvider, usePageHeaderValue } from '../context/PageHeaderContext';

export default function Layout() {
  return (
    <PageHeaderProvider>
      <LayoutInner />
    </PageHeaderProvider>
  );
}

function LayoutInner() {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { header } = usePageHeaderValue();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [unreadWhatsapp, setUnreadWhatsapp] = useState(0);

  // Cantidad de conversaciones de WhatsApp con un mensaje del cliente que todavía no se ha
  // abierto (lastMessageAt más nuevo que lastReadAt, que se actualiza al abrir la
  // conversación en Mensajería) — aviso de "mensajes nuevos" en el ícono del sidebar. En
  // vivo vía onSnapshot, no hace falta estar en esa pantalla para verlo llegar.
  useEffect(() => {
    const q = query(collection(db, 'whatsapp_conversations'), where('lastMessageDirection', '==', 'incoming'));
    const unsubscribe = onSnapshot(q, (snap) => {
      const count = snap.docs.filter(d => {
        const data = d.data() as any;
        if (!data.lastMessageAt) return true;
        if (!data.lastReadAt) return true;
        return data.lastMessageAt.toMillis() > data.lastReadAt.toMillis();
      }).length;
      setUnreadWhatsapp(count);
    }, () => setUnreadWhatsapp(0));
    return () => unsubscribe();
  }, []);

  // Close profile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen flex bg-background text-foreground transition-colors duration-200">
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 transition-opacity xl:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 transform transition-transform duration-200 ease-in-out xl:translate-x-0 xl:static xl:inset-auto flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Sidebar Header / Logo */}
        <div className="flex items-center justify-between py-6 px-5 border-b border-gray-100 dark:border-gray-900">
          <NavLink to="/" className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
            <img src="/logo.png" alt="Logo" className="w-9 h-9 object-contain rounded-xl" />
            <div>
              <h1 className="font-bold text-lg tracking-tight text-gray-900 dark:text-white">CobrApp</h1>
              <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold font-mono block -mt-1">Web Admin</span>
            </div>
          </NavLink>
          <button
            onClick={() => setSidebarOpen(false)}
            className="xl:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            title="Cerrar menú"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1.5 overflow-y-auto">
          <NavLink
            to="/"
            end
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                isActive 
                  ? 'bg-violet-50 dark:bg-violet-500/15 text-violet-600 dark:text-violet-400 font-semibold' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-gray-900 dark:hover:text-white'
              }`
            }
          >
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </NavLink>
          
          <NavLink
            to="/admins"
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                isActive 
                  ? 'bg-violet-50 dark:bg-violet-500/15 text-violet-600 dark:text-violet-400 font-semibold' 
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-gray-900 dark:hover:text-white'
              }`
            }
          >
            <Users size={18} />
            <span>Administradores</span>
          </NavLink>

          {/* Remarketing: seccion con submenus, no un link propio */}
          <div className="pt-2">
            <div className="flex items-center gap-2.5 px-4 py-1.5 text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              <Sparkles size={14} />
              <span>Remarketing</span>
            </div>
            <div className="flex flex-col gap-1 pl-3">
              <NavLink
                to="/remarketing/newsletter"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                    isActive
                      ? 'bg-violet-50 dark:bg-violet-500/15 text-violet-600 dark:text-violet-400 font-semibold'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-gray-900 dark:hover:text-white'
                  }`
                }
              >
                <Mail size={16} />
                <span>Newsletter</span>
              </NavLink>
              <NavLink
                to="/remarketing/mensajeria"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                    isActive
                      ? 'bg-violet-50 dark:bg-violet-500/15 text-violet-600 dark:text-violet-400 font-semibold'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-gray-900 dark:hover:text-white'
                  }`
                }
              >
                <MessageCircle size={16} />
                <span>Mensajería</span>
                {unreadWhatsapp > 0 && (
                  <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {unreadWhatsapp > 99 ? '99+' : unreadWhatsapp}
                  </span>
                )}
              </NavLink>
            </div>
          </div>

        </nav>

        {/* Pro Banner (Easter Egg matching Fundex design) */}
        <div className="px-4 py-4">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-gray-900 to-violet-950 p-4 border border-gray-800 shadow-lg">
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-28 h-28 bg-violet-500/30 blur-3xl rounded-full pointer-events-none" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="text-white fill-white w-4 h-4" />
                <h4 className="text-white text-sm font-semibold">CobrApp Pro</h4>
              </div>
              <p className="text-gray-400 text-[11px] leading-relaxed mb-3">
                Accede a análisis geográficos avanzados y reportes de facturación detallados.
              </p>
              <button 
                onClick={() => navigate('/')} 
                className="w-full bg-violet-600 text-white text-xs font-semibold py-2 px-3 rounded-xl hover:bg-violet-700 transition-colors shadow-md shadow-violet-950/50 cursor-pointer"
              >
                Actualizar a Pro
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-900">
          <button
            onClick={logout}
            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl transition-colors cursor-pointer"
          >
            <LogOut size={18} />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      {/* Main Container */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden relative">
        
        {/* Sticky Header */}
        <header className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-30 w-full shrink-0">
          <div className="py-3.5 px-4 md:px-6 flex items-center justify-between">
            
            {/* Header Left (Título de la página / Mobile toggle) */}
            <div className="flex items-center flex-1 gap-4 min-w-0">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="xl:hidden inline-flex items-center justify-center rounded-xl p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-900 hover:text-gray-900 dark:hover:text-white transition-colors"
                aria-label="Open sidebar"
              >
                <Menu size={20} />
              </button>

              {header && (
                <div className="min-w-0">
                  <h1 className="text-base md:text-lg font-extrabold text-gray-900 dark:text-white tracking-tight truncate flex items-center gap-2">
                    {header.title}
                  </h1>
                  {header.subtitle && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate hidden sm:block">{header.subtitle}</p>
                  )}
                </div>
              )}
            </div>

            {/* Header Right */}
            <div className="flex items-center gap-3.5">
              
              {/* Theme Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-900 rounded-xl text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                title={darkMode ? 'Modo Claro' : 'Modo Oscuro'}
                type="button"
              >
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {/* Notification Bell */}
              <div className="relative">
                <button
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-900 rounded-xl text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                  title="Notificaciones"
                >
                  <Bell size={18} />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-white dark:border-gray-950" />
                </button>
              </div>

              {/* User Dropdown */}
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center pl-1 pr-3 py-1 cursor-pointer bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800/80 rounded-xl h-10 border border-gray-200 dark:border-gray-800 transition-all"
                >
                  <div className="size-7.5 rounded-lg bg-violet-600 text-white flex items-center justify-center font-bold text-xs uppercase shadow-sm">
                    {user?.email?.charAt(0) || 'A'}
                  </div>
                  <span className="text-xs font-medium ml-2 text-gray-700 dark:text-gray-300 hidden sm:block">
                    {user?.email?.split('@')[0] || 'Admin'}
                  </span>
                  <ChevronDown 
                    size={14} 
                    className={`ml-2 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${
                      profileMenuOpen ? 'rotate-180' : ''
                    }`} 
                  />
                </button>

                {profileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-lg py-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800">
                      <p className="text-xs text-gray-400">Sesión iniciada como</p>
                      <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{user?.email}</p>
                    </div>
                    
                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate('/');
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
                    >
                      <User size={14} />
                      <span>Mi Cuenta</span>
                    </button>

                    <hr className="border-gray-100 dark:border-gray-800 my-1" />

                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        logout();
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 text-left font-medium"
                    >
                      <LogOut size={14} />
                      <span>Cerrar Sesión</span>
                    </button>
                  </div>
                )}
              </div>

            </div>
          </div>
        </header>

        {/* Scrollable Main Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto relative bg-background">
          <div className="max-w-[1600px] w-full mx-auto p-4 md:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
